import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ModalContext from 'contexts/ModalContext'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import noop from 'lib/function/noop'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import ConfirmDeleteChatModal from 'tripPlanner/components/Chat/ConfirmDeleteChatModal'
import type { useRecentTrips } from 'tripPlanner/hooks/api/trip'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'
import { formatChatLabel } from './utils/format'
import { findClosestChat } from './utils/findClosestChat'
import ChatLandingPage from '../ChatLandingPage/ChatLandingPage'
import PromptModal, { type PromptModalResult } from '../PromptModal'
import TripPlannerHeader from '../TripPlannerHeader'
import PaneResizer from 'tripPlanner/components/PaneResizer'
import { clamp } from 'lib/maths/mathUtils'
import {
  MainLayout,
  ContentWrapper,
  MainContent,
  ChatColumnLayout,
  LandingPageWrapper,
  CANVAS_MIN_WIDTH,
  CHAT_MIN_WIDTH, CHAT_DEFAULT_WIDTH_PERCENT,
  LEFT_PANE_WIDTH_VAR,
} from './styles/layoutStyles'
import { DrawerAskAIProvider } from './contexts/DrawerAskAIContext'
import { useTripPlannerEventBus } from './contexts/TripPlannerEventBus'
import { useChatActions } from './contexts/ChatActionsContext'
import { useChatStreamStatus } from './contexts/ChatStreamStatusContext'
import { useChatFiles } from './contexts/ChatFilesContext'
import { useChatStreamMessages } from './contexts/ChatStreamMessagesContext'
import { MapInteractionProvider } from './contexts/MapInteractionContext'
import type { ChatNavigationOptions } from './hooks/useChatNavigation'
import { useMessageContext } from './hooks/useMessageContext'
import ChatContainer from './containers/ChatContainer'
import RightPanelContainer from './containers/RightPanelContainer'
import DrawerContainer from './containers/DrawerContainer'

// ── Props interface ──

interface Props {
  chatId: number | undefined
  syncChatId: (id: number | undefined, options?: ChatNavigationOptions) => void
  isNewChatRoute: boolean
  isLandingRoute: boolean
  existingRouteChatId: number | undefined
  navigateToNewChatRoute: () => void
  chatHistory: Array<TripPlannerChat>
  recentChats: Array<TripPlannerChat>
  refetchChatList: () => Promise<unknown>
  deleteChatMutation: (params: { chatId: number }) => Promise<unknown>
  previousTrips: ReturnType<typeof useRecentTrips>['data']
  deleteTripMutation: (tripId: string) => Promise<unknown>
  isLoggedIn: boolean
  isStaff: boolean
  isDevMode: boolean
  onDevModeToggle: () => void
  canManagePrompts: boolean
  promptOptions: Array<{ key: string; name: string; promptId?: string; isDefault?: boolean; notes?: string }>
  selectedPromptKey: string
  handlePromptAdd: (result: PromptModalResult) => Promise<unknown>
  handlePromptSelect: (key: string) => void
  loadPrompts: () => void
  setBookingSelection: React.Dispatch<React.SetStateAction<{ orderIds: Array<string>; offerIds: Array<string> }>>
}

// ── Orchestrator: reads from contexts, wires containers ──

function ChatPageContent({
  chatId,
  syncChatId,
  isNewChatRoute,
  isLandingRoute,
  existingRouteChatId,
  navigateToNewChatRoute,
  chatHistory,
  recentChats,
  refetchChatList,
  deleteChatMutation,
  previousTrips,
  deleteTripMutation,
  isLoggedIn,
  isStaff,
  isDevMode,
  onDevModeToggle,
  canManagePrompts,
  promptOptions,
  selectedPromptKey,
  handlePromptAdd,
  handlePromptSelect,
  loadPrompts,
  setBookingSelection,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const eventBus = useTripPlannerEventBus()

  // ── Read from contexts ──

  const { submitMessage, resetConversation, resetStreamingState, setFollowTail } = useChatActions()
  const { isStreaming, loadingState } = useChatStreamStatus()
  const { files, setFiles } = useChatFiles()

  const { messages } = useChatStreamMessages()

  // ── Hover state (MapInteractionContext) ──

  const [hoveredHotelId, setHoveredHotelId] = useState<string | null>(null)
  const isCardHoveredRef = useRef(false)

  const handleMarkerHover = useCallback((markerId: string | null) => {
    if (markerId === null && isCardHoveredRef.current) return
    setHoveredHotelId(markerId)
  }, [])

  const handleCardHover = useCallback((offerId: string | null) => {
    isCardHoveredRef.current = offerId !== null
    setHoveredHotelId(offerId)
  }, [])

  const mapInteractionValue = useMemo(() => ({
    onCardHover: handleCardHover,
    onMarkerHover: handleMarkerHover,
    highlightedMarkerId: hoveredHotelId,
  }), [handleCardHover, handleMarkerHover, hoveredHotelId])

  // ── Pane resizer ──

  const mainContentRef = useRef<HTMLDivElement>(null)
  const leftPaneWidthPercentage = useRef<number>(CHAT_DEFAULT_WIDTH_PERCENT)

  const handlePaneDragStart = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()

    const handleDragging = (e: MouseEvent) => {
      if (!mainContentRef.current) return
      const rect = mainContentRef.current.getBoundingClientRect()
      const containerWidth = rect.width
      const minLeft = CHAT_MIN_WIDTH
      const maxLeft = containerWidth - CANVAS_MIN_WIDTH
      const newWidth = clamp(minLeft, e.clientX - rect.left, maxLeft)
      const newWidthPercentage = 100 * (newWidth / containerWidth)
      leftPaneWidthPercentage.current = newWidthPercentage
      mainContentRef.current.style.setProperty(LEFT_PANE_WIDTH_VAR, `${newWidthPercentage}%`)
    }

    const handleDragEnd = () => {
      window.removeEventListener('mousemove', handleDragging)
      window.removeEventListener('mouseup', handleDragEnd)
    }

    window.addEventListener('mousemove', handleDragging)
    window.addEventListener('mouseup', handleDragEnd)
  }, [])

  // ── chatIdRef for async operations ──

  const chatIdRef = useRef(chatId)
  useEffect(() => {
    chatIdRef.current = chatId
  }, [chatId])

  // ── useMessageContext (stays in orchestrator — needed by both landing page and ChatContainer) ──

  const isLoading = loadingState.status === 'loadingHistory' || loadingState.status === 'preparingStream'

  const focusChatInput = useCallback(() => {
    eventBus.dispatch({ type: 'focus_chat_input' })
  }, [eventBus])

  const {
    activeMultiChoiceSelection,
    mobileSelectionText,
    offerContextTag,
    handleSubmit,
    handleHotelOffersDisplayed,
    handleMultiChoiceSelectionChange,
    handleRemoveTag,
    pendingHotelOfferIdsRef,
    resetSelectionState,
  } = useMessageContext({
    streamingSubmit: submitMessage,
    setFollowTail,
    // ChatContainer owns messageInputValue state and ChatMessageInput clears itself
    // on submit. The orchestrator doesn't need a real setter here.
    setMessageInputValue: noop,
    focusChatInput,
  })

  const handleAddTags = useCallback((tags: Array<string>) => {
    const deduplicated = tags.filter(tag => !activeMultiChoiceSelection.includes(tag))
    if (deduplicated.length > 0) {
      handleMultiChoiceSelectionChange([...activeMultiChoiceSelection, ...deduplicated])
    }
  }, [handleMultiChoiceSelectionChange, activeMultiChoiceSelection])

  const landingPageTags = activeMultiChoiceSelection

  const handleOptionSelect = useCallback((optionText: string) => {
    handleSubmit(optionText)
  }, [handleSubmit])

  const handleChatPanelOptionSelect = useCallback((optionText: string) => {
    if (optionText.match(/^\d+\.\s/)) {
      handleHotelOffersDisplayed(optionText)
    } else {
      handleOptionSelect(optionText)
    }
  }, [handleHotelOffersDisplayed, handleOptionSelect])

  // ── Chat navigation ──

  const resetChatSpecificState = useCallback(() => {
    eventBus.dispatch({ type: 'reset_chat_state' })
    // Reset orchestrator-local state
    resetSelectionState()
    pendingHotelOfferIdsRef.current = null
    setHoveredHotelId(null)
    isCardHoveredRef.current = false
  }, [eventBus, resetSelectionState, pendingHotelOfferIdsRef])

  const resetConversationState = useCallback((options?: ChatNavigationOptions) => {
    syncChatId(undefined, { shouldUpdateUrl: options?.shouldUpdateUrl })
    resetConversation({ followTail: false })
    resetChatSpecificState()
  }, [syncChatId, resetConversation, resetChatSpecificState])

  const handleStartNewChat = useCallback(() => {
    resetConversationState({ shouldUpdateUrl: false })
    navigateToNewChatRoute()
  }, [navigateToNewChatRoute, resetConversationState])

  const loadChatById = useCallback((selectedChatId: number, options?: ChatNavigationOptions) => {
    if (selectedChatId === undefined || selectedChatId === chatId) {
      return
    }

    resetChatSpecificState()
    resetStreamingState()
    chatIdRef.current = selectedChatId
    syncChatId(selectedChatId, { shouldUpdateUrl: options?.shouldUpdateUrl })
    setFollowTail(false)
  }, [
    chatId,
    setFollowTail,
    syncChatId,
    resetChatSpecificState,
    resetStreamingState,
  ])

  const handleSelectChatFromHistory = useCallback((selectedChatId: number) => {
    loadChatById(selectedChatId)
  }, [loadChatById])

  // ── Route change effect ──

  useEffect(() => {
    if (isNewChatRoute) {
      if (chatId) {
        resetConversationState({ shouldUpdateUrl: false })
      }
      return
    }

    if (existingRouteChatId !== undefined) {
      if (existingRouteChatId !== chatId) {
        loadChatById(existingRouteChatId, { shouldUpdateUrl: false })
      }
      return
    }

    if (isLandingRoute && chatId) {
      resetConversationState({ shouldUpdateUrl: false })
    }
  }, [isNewChatRoute, existingRouteChatId, isLandingRoute, chatId, loadChatById, resetConversationState])

  // ── Prompt loading ──

  useEffect(() => {
    loadPrompts()
  }, [loadPrompts])

  // ── Delete handlers ──

  const thisChatFallback = intl.formatMessage({
    id: '-LmD19gmaBgkOCUWrnKU',
    defaultMessage: 'this chat',
    description: 'Fallback display name for a trip planner chat when no chat is found',
  })

  const getChatDisplayName = useCallback((chat: TripPlannerChat | undefined) => {
    if (!chat) {
      return thisChatFallback
    }
    const trimmedName = chat.name?.trim()
    if (trimmedName) {
      return trimmedName
    }
    return formatChatLabel(getChatLastActivityTimestamp(chat), intl)
  }, [intl, thisChatFallback])

  const findClosestChatId = useCallback((chatIdToCompare: number) => {
    return findClosestChat(chatHistory, chatIdToCompare)
  }, [chatHistory])

  const handleDeleteChat = useCallback(async(chatIdToDelete: number) => {
    const chatToDelete = chatHistory.find((chat) => chat.id === chatIdToDelete)
    const chatName = getChatDisplayName(chatToDelete)

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={chatName} />,
    )

    if (!confirmed) {
      return
    }

    const fallbackChatId = chatId === chatIdToDelete ? findClosestChatId(chatIdToDelete) : undefined

    try {
      await deleteChatMutation({ chatId: chatIdToDelete })
      if (chatId === chatIdToDelete) {
        if (fallbackChatId) {
          loadChatById(fallbackChatId)
        } else {
          resetConversationState()
        }
      }
      await refetchChatList()
    } catch(error) {
      console.error('[TripChat] Failed to delete chat', error)
      showSnackbar(intl.formatMessage({
        id: 'GjRGiJeZErGMMw8bQjrE',
        defaultMessage: 'Unable to delete chat. Please try again.',
        description: 'Error snackbar message when chat deletion fails in the trip planner AI chat',
      }), 'critical')
    }
  }, [chatHistory, showModal, deleteChatMutation, chatId, resetConversationState, refetchChatList, getChatDisplayName, findClosestChatId, loadChatById, intl])

  const handleDeleteTrip = useCallback(async(tripIdToDelete: string) => {
    const tripToDelete = previousTrips?.find((trip) => trip.id === tripIdToDelete)
    const untitledTripFallback = intl.formatMessage({
      id: 'qNXsaB7GsvlaNr5QLIZf',
      defaultMessage: 'Untitled Trip',
      description: 'Fallback name for a trip without a title in the delete confirmation modal',
    })
    const tripName = tripToDelete?.name ?? untitledTripFallback

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={tripName} type="trip" />,
    )

    if (!confirmed) {
      return
    }

    await deleteTripMutation(tripIdToDelete)
  }, [previousTrips, showModal, deleteTripMutation, intl])

  const handlePromptAddWithModal = useCallback(async() => {
    const result = await showModal<PromptModalResult | undefined>(<PromptModal />)
    if (result) {
      await handlePromptAdd(result)
    }
  }, [showModal, handlePromptAdd])

  // ── Header ──

  const tripPlannerHeader = useMemo(() => (
    <TripPlannerHeader
      isLoggedIn={isLoggedIn}
      recentChats={recentChats}
      recentTrips={previousTrips}
      onStartNewChat={handleStartNewChat}
      onChatSelect={handleSelectChatFromHistory}
      onChatDelete={handleDeleteChat}
      onTripDelete={handleDeleteTrip}
      isStaff={isStaff}
      isDevMode={isDevMode}
      onDevModeToggle={onDevModeToggle}
      canManagePrompts={canManagePrompts}
      prompts={promptOptions}
      selectedPromptKey={selectedPromptKey}
      onPromptSelect={handlePromptSelect}
      onPromptAdd={handlePromptAddWithModal}
    />
  ), [isLoggedIn, recentChats, previousTrips, handleStartNewChat, handleSelectChatFromHistory, handleDeleteChat, handleDeleteTrip, isStaff, isDevMode, onDevModeToggle, canManagePrompts, promptOptions, selectedPromptKey, handlePromptSelect, handlePromptAddWithModal])

  // ── Render ──

  const hasMessages = messages.length > 0
  const isLandingExperience = isLandingRoute && !hasMessages
  const isNewChatExperience = isNewChatRoute && !hasMessages
  const shouldShowNewChatComposer = isNewChatExperience

  // Landing page or new chat — identical UI
  if (isLandingExperience || shouldShowNewChatComposer) {
    return (
      <MainLayout>
        {tripPlannerHeader}
        <LandingPageWrapper>
          <ChatLandingPage
            onSubmit={handleSubmit}
            onAddTags={handleAddTags}
            disabled={isLoading}
            files={files}
            onFilesChange={setFiles}
            selectedTags={landingPageTags}
            onRemoveTag={handleRemoveTag}
            onBookingSelectionChange={setBookingSelection}
          />
        </LandingPageWrapper>
      </MainLayout>
    )
  }

  return (
    <MainLayout>
      {tripPlannerHeader}
      <MapInteractionProvider value={mapInteractionValue}>
        <DrawerAskAIProvider
          submitMessage={submitMessage}
          messages={messages}
          isStreaming={isStreaming}
        >
          <ContentWrapper>
            <MainContent ref={mainContentRef} style={{ [LEFT_PANE_WIDTH_VAR]: `${leftPaneWidthPercentage.current}%` }}>
              <ChatColumnLayout>
                <ChatContainer
                  chatId={chatId}
                  isDevMode={isDevMode}
                  navigateToNewChatRoute={navigateToNewChatRoute}
                  handleSubmit={handleSubmit}
                  handleChatPanelOptionSelect={handleChatPanelOptionSelect}
                  handleMultiChoiceSelectionChange={handleMultiChoiceSelectionChange}
                  activeMultiChoiceSelection={activeMultiChoiceSelection}
                  mobileSelectionText={mobileSelectionText}
                  offerContextTag={offerContextTag}
                  handleRemoveTag={handleRemoveTag}
                />
              </ChatColumnLayout>
              <PaneResizer onMouseDown={handlePaneDragStart} />
              <RightPanelContainer />
              <DrawerContainer />
            </MainContent>
          </ContentWrapper>
        </DrawerAskAIProvider>
      </MapInteractionProvider>
    </MainLayout>
  )
}

export default ChatPageContent
